import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import HelpIcon from '../Common/HelpIcon';
import './RadioTable.css'; // Reusing RadioTable styles

interface SportSignageSpot {
  start_date: string;
  end_date: string;
  time_et: string;
  away_team: string;
  home_team: string;
  conference: string;
  tv_network: string;
  impressions: number;
  home_dma_name: string;
  away_dma_name: string;
}

interface SportSignageTableProps {
  data: SportSignageSpot[];
  itemsPerPage?: number;
  onHelpClick?: () => void;
}

// Phase 1.3: Memoized to prevent unnecessary re-renders
const SportSignageTable: React.FC<SportSignageTableProps> = React.memo(({ data, itemsPerPage = 10, onHelpClick }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<keyof SportSignageSpot>('start_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (field: keyof SportSignageSpot) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'MMM dd, yyyy');
  };

  const getVisiblePages = () => {
    const delta = isMobile ? 1 : 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    if (isMobile && rangeWithDots.length > 5) {
      if (currentPage <= 3) {
        return [1, 2, 3, '...', totalPages];
      } else if (currentPage >= totalPages - 2) {
        return [1, '...', totalPages - 2, totalPages - 1, totalPages];
      } else {
        return [1, '...', currentPage, '...', totalPages];
      }
    }

    return rangeWithDots;
  };

  return (
    <div className="radio-table-container">
      <div className="radio-table-header">
        <h3 className="radio-table-title">
          Sport Signage – MLB & NCAA
          {onHelpClick && <HelpIcon onClick={onHelpClick} />}
        </h3>
        <div className="radio-table-info">
          Showing {startIndex + 1}-{Math.min(endIndex, data.length)} of {data.length} events
        </div>
      </div>

      <div className="radio-table-wrapper">
        <table className="radio-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('start_date')} className="sortable">
                Start Date {sortField === 'start_date' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('end_date')} className="sortable">
                End Date {sortField === 'end_date' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('home_team')} className="sortable">
                Home Team {sortField === 'home_team' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('away_team')} className="sortable">
                Away Team {sortField === 'away_team' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('conference')} className="sortable">
                League {sortField === 'conference' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('home_dma_name')} className="sortable">
                Market {sortField === 'home_dma_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('impressions')} className="sortable">
                Impressions {sortField === 'impressions' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((spot, index) => (
              <tr key={index}>
                <td>{formatDate(spot.start_date)}</td>
                <td>{spot.start_date === spot.end_date ? '—' : formatDate(spot.end_date)}</td>
                <td>{spot.home_team}</td>
                <td>{spot.away_team}</td>
                <td>{spot.conference}</td>
                <td>{spot.home_dma_name || '—'}</td>
                <td>{spot.impressions.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="radio-table-pagination">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="material-icons">chevron_left</i>
            <span>Previous</span>
          </button>

          <div className="pagination-pages">
            {totalPages > 7 ? (
              getVisiblePages().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page as number)}
                    className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                )
              ))
            ) : (
              Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              ))
            )}
          </div>

          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <span>Next</span>
            <i className="material-icons">chevron_right</i>
          </button>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Phase 1.3: Only re-render if data changed
  return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);
});

export default SportSignageTable;
